import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Sprout, Plus, Crown, ArrowLeft, Sparkles, Trash2, Printer, LayoutGrid, Square, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { BedDiagram } from '@/components/BedDiagram';
import { UpgradeDialog } from '@/components/UpgradeDialog';
import { LegalFooter } from '@/components/LegalFooter';
import type { BedSpec, BedDesign } from '@/lib/companionPlanting';
import { TEMPLATES, GuildTemplate, findTemplatesForHero } from '@/lib/companionTemplates';
import {
  buildDesignFromTemplate,
  missingCompanions,
  Density,
  MissingDecision,
} from '@/lib/companionDesigner';
import { getPlantImage, getPlantEmoji } from '@/lib/plantEmojis';

interface Bed {
  id: string;
  name: string;
  length_cm: number;
  width_cm: number;
  sun_exposure: 'full_sun' | 'partial_shade' | 'shade';
  soil_notes: string | null;
}

interface DBPlant { id: string; name: string; category: string; }
interface UserPlant { id: string; plant_id: string; status: string; }

const SUN_LABEL: Record<string, string> = {
  full_sun: '☀ Full sun (6+ hrs)',
  partial_shade: '⛅ Partial shade (3–6 hrs)',
  shade: '🌥 Shade (<3 hrs)',
};

const DENSITY_LABEL: Record<Density, string> = {
  sparse: 'Sparse — roomier, easier to weed',
  balanced: 'Balanced — classic spacing',
  packed: 'Packed — square-foot intensive',
};

interface SavedDesign extends BedDesign {
  templateId?: string;
  templateName?: string;
  heroPlant?: string;
  density?: Density;
  missingDecisions?: MissingDecision[];
}

export default function Companion() {
  const { user } = useAuth();
  const { isPro, loading: subLoading } = useSubscription();
  const navigate = useNavigate();

  const [beds, setBeds] = useState<Bed[]>([]);
  const [plants, setPlants] = useState<DBPlant[]>([]);
  const [userPlants, setUserPlants] = useState<UserPlant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [selectedBedId, setSelectedBedId] = useState<string | null>(null);
  const [designs, setDesigns] = useState<Record<string, SavedDesign>>({});
  const [view, setView] = useState<'single' | 'garden'>('single');

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState<'hero' | 'density' | 'missing'>('hero');
  const [wizardHero, setWizardHero] = useState<string | null>(null);
  const [wizardTemplate, setWizardTemplate] = useState<GuildTemplate | null>(null);
  const [wizardDensity, setWizardDensity] = useState<Density>('balanced');
  const [wizardDecisions, setWizardDecisions] = useState<Record<string, 'add' | 'skip'>>({});
  const [busy, setBusy] = useState(false);

  const [showNewBed, setShowNewBed] = useState(false);
  const [form, setForm] = useState({
    name: '', length_cm: 200, width_cm: 100, sun_exposure: 'full_sun', soil_notes: '',
  });

  useEffect(() => { if (user) void loadAll(); }, [user]);

  const loadAll = async () => {
    setLoading(true);
    const [bedsRes, plantsRes, userPlantsRes, designsRes] = await Promise.all([
      supabase.from('garden_beds' as any).select('*').order('created_at', { ascending: true }),
      supabase.from('plants').select('id, name, category'),
      supabase.from('user_plants').select('id, plant_id, status'),
      supabase.from('companion_designs' as any).select('*'),
    ]);
    if (bedsRes.error) toast.error('Could not load beds');
    if (plantsRes.error) toast.error('Could not load plants');
    if (userPlantsRes.error) toast.error('Could not load your garden');

    const loadedBeds = (bedsRes.data ?? []) as unknown as Bed[];
    setBeds(loadedBeds);
    setPlants((plantsRes.data ?? []) as unknown as DBPlant[]);
    setUserPlants((userPlantsRes.data ?? []) as unknown as UserPlant[]);

    const loaded: Record<string, SavedDesign> = {};
    for (const row of (designsRes.data ?? []) as any[]) {
      const layout = row.layout ?? {};
      const plan = (row.plan as any)?.items ?? [];
      const placements = layout.placements ?? [];
      const spec = layout.spec;
      if (spec) {
        loaded[row.bed_id] = {
          bedId: row.bed_id,
          bedName: spec.name,
          spec,
          placements,
          plan,
          rationale: layout.rationale ?? [],
          warnings: layout.warnings ?? [],
          templateId: row.template_id ?? undefined,
          templateName: row.template_name ?? undefined,
          heroPlant: row.hero_plant ?? undefined,
          density: row.density ?? 'balanced',
          missingDecisions: row.missing_decisions?.items ?? [],
        };
      }
    }
    setDesigns(loaded);
    if (loadedBeds.length > 0 && !selectedBedId) setSelectedBedId(loadedBeds[0].id);
    setLoading(false);
  };

  const userPlantNames = useMemo(() => {
    const byId = new Map(plants.map((p) => [p.id, p.name]));
    const set = new Set<string>();
    for (const up of userPlants) {
      const n = byId.get(up.plant_id);
      if (n) set.add(n);
    }
    return Array.from(set);
  }, [plants, userPlants]);

  // Plants used as hero in OTHER beds → grey them out in wizard
  const heroesUsedElsewhere = useMemo(() => {
    const set = new Set<string>();
    for (const [bedId, d] of Object.entries(designs)) {
      if (bedId === selectedBedId) continue;
      if (d.heroPlant) set.add(d.heroPlant.toLowerCase());
    }
    return set;
  }, [designs, selectedBedId]);

  const selectedBed = beds.find((b) => b.id === selectedBedId) ?? null;
  const design = selectedBedId ? designs[selectedBedId] ?? null : null;

  // Templates available for the chosen hero
  const templatesForHero = useMemo(() => {
    if (!wizardHero) return [];
    return findTemplatesForHero(wizardHero);
  }, [wizardHero]);

  // ---------- bed CRUD ----------
  const handleCreateBed = async () => {
    if (!isPro) { setShowUpgrade(true); return; }
    if (!form.name.trim()) { toast.error('Give your bed a name'); return; }
    if (form.length_cm < 30 || form.width_cm < 30) { toast.error('Bed must be at least 30×30 cm'); return; }
    const { data, error } = await supabase
      .from('garden_beds' as any)
      .insert({
        user_id: user!.id,
        name: form.name.trim(),
        length_cm: form.length_cm,
        width_cm: form.width_cm,
        sun_exposure: form.sun_exposure,
        soil_notes: form.soil_notes.trim() || null,
      } as any)
      .select()
      .single();
    if (error) { toast.error(error.message); return; }
    const newBed = data as unknown as Bed;
    setBeds((p) => [...p, newBed]);
    setSelectedBedId(newBed.id);
    setShowNewBed(false);
    setForm({ name: '', length_cm: 200, width_cm: 100, sun_exposure: 'full_sun', soil_notes: '' });
    toast.success('Bed added');
  };

  const handleDeleteBed = async (id: string) => {
    if (!confirm('Delete this bed?')) return;
    const { error } = await supabase.from('garden_beds' as any).delete().eq('id', id);
    if (error) { toast.error(error.message); return; }
    await supabase.from('companion_designs' as any).delete().eq('bed_id', id);
    setBeds((prev) => prev.filter((b) => b.id !== id));
    setDesigns((prev) => { const next = { ...prev }; delete next[id]; return next; });
    if (selectedBedId === id) setSelectedBedId(beds.find((b) => b.id !== id)?.id ?? null);
  };

  // ---------- wizard ----------
  const openWizard = () => {
    if (!selectedBed) { toast.error('Pick a bed first'); return; }
    setWizardHero(null);
    setWizardTemplate(null);
    setWizardDensity('balanced');
    setWizardDecisions({});
    setWizardStep('hero');
    setWizardOpen(true);
  };

  const handlePickHero = (plantName: string) => {
    const tmpls = findTemplatesForHero(plantName);
    if (tmpls.length === 0) {
      toast.error(`No template for ${plantName} yet — try another hero crop.`);
      return;
    }
    setWizardHero(plantName);
    // Auto-pick the first matching template (most have only one hero match).
    setWizardTemplate(tmpls[0]);
    setWizardStep('density');
  };

  const proceedToMissingOrBuild = async () => {
    if (!wizardTemplate || !wizardHero) return;
    const missing = missingCompanions(wizardTemplate, userPlantNames, wizardHero).filter(
      (s) => !s.required,
    );
    // Initialise decisions with default 'skip' for each missing companion
    if (missing.length > 0) {
      const init: Record<string, 'add' | 'skip'> = {};
      for (const m of missing) init[m.name] = 'skip';
      setWizardDecisions(init);
      setWizardStep('missing');
    } else {
      await buildAndSave([]);
    }
  };

  const buildAndSave = async (decisions: MissingDecision[]) => {
    if (!user || !selectedBed || !wizardTemplate || !wizardHero) return;
    setBusy(true);
    try {
      const spec: BedSpec = {
        id: selectedBed.id,
        name: selectedBed.name,
        length_cm: selectedBed.length_cm,
        width_cm: selectedBed.width_cm,
        sun_exposure: selectedBed.sun_exposure,
      };

      // Add 'add' decisions to user's garden as planned plants
      for (const d of decisions) {
        if (d.decision !== 'add') continue;
        const dbPlant = plants.find((p) => p.name.toLowerCase() === d.name.toLowerCase());
        if (!dbPlant) continue;
        if (userPlantNames.some((n) => n.toLowerCase() === d.name.toLowerCase())) continue;
        await supabase.from('user_plants').insert({
          user_id: user.id,
          plant_id: dbPlant.id,
          status: 'planned',
          setup_type: 'direct',
        });
      }

      const result = buildDesignFromTemplate({
        bed: spec,
        template: wizardTemplate,
        density: wizardDensity,
        userPlantNames: [
          ...userPlantNames,
          ...decisions.filter((d) => d.decision === 'add').map((d) => d.name),
        ],
        missingDecisions: decisions,
        heroPlant: wizardHero,
      });

      const layout = {
        spec: result.spec,
        placements: result.placements,
        rationale: result.rationale,
        warnings: result.warnings,
      };

      await supabase
        .from('companion_designs' as any)
        .delete()
        .eq('bed_id', selectedBed.id)
        .eq('user_id', user.id);
      await supabase.from('companion_designs' as any).insert({
        user_id: user.id,
        bed_id: selectedBed.id,
        layout,
        plan: { items: result.plan },
        rationale: result.rationale.join('\n'),
        template_id: wizardTemplate.id,
        template_name: wizardTemplate.name,
        hero_plant: wizardHero,
        density: wizardDensity,
        missing_decisions: { items: decisions },
      } as any);

      const saved: SavedDesign = {
        ...result,
        templateId: wizardTemplate.id,
        templateName: wizardTemplate.name,
        heroPlant: wizardHero,
        density: wizardDensity,
        missingDecisions: decisions,
      };
      setDesigns((prev) => ({ ...prev, [selectedBed.id]: saved }));
      setWizardOpen(false);
      toast.success(`${wizardTemplate.name} designed for ${selectedBed.name}`);
      // Refresh user plants if we added any
      if (decisions.some((d) => d.decision === 'add')) await loadAll();
    } catch (e: any) {
      toast.error(e.message ?? 'Could not build design');
    } finally {
      setBusy(false);
    }
  };

  const finishMissingStep = () => {
    const decisions: MissingDecision[] = Object.entries(wizardDecisions).map(([name, decision]) => ({
      name, decision,
    }));
    void buildAndSave(decisions);
  };

  const handlePrint = () => window.print();

  if (subLoading || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Sprout className="h-8 w-8 animate-pulse text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b border-border/40 bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="rounded-xl">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Roboto, sans-serif' }}>
              Companion Planting
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {isPro && Object.keys(designs).length > 0 && (
              <>
                <Button
                  variant="outline" size="sm"
                  onClick={() => setView(view === 'single' ? 'garden' : 'single')}
                  className="gap-1 rounded-xl print:hidden"
                >
                  {view === 'single'
                    ? <><LayoutGrid className="h-3.5 w-3.5" /> Whole garden</>
                    : <><Square className="h-3.5 w-3.5" /> Single bed</>}
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint} className="gap-1 rounded-xl print:hidden">
                  <Printer className="h-3.5 w-3.5" /> Print
                </Button>
              </>
            )}
            {!isPro && (
              <Button variant="outline" size="sm" onClick={() => setShowUpgrade(true)} className="gap-1 rounded-xl">
                <Crown className="h-3.5 w-3.5" /> Upgrade
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 space-y-6">
        {!isPro ? (
          <Card className="border-2 border-primary/30 shadow-medium">
            <CardContent className="pt-8 pb-6 text-center space-y-4">
              <div className="mx-auto h-14 w-14 rounded-2xl garden-gradient flex items-center justify-center shadow-soft">
                <Sparkles className="h-7 w-7 text-primary-foreground" />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Roboto, sans-serif' }}>
                  Design dense, beautiful raised beds
                </h2>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Pick a hero crop. We'll wrap it in a hand-curated guild of companions
                  proven to work together — Three Sisters, Tomato Trio, Salad Bed and more.
                </p>
              </div>
              <Button onClick={() => setShowUpgrade(true)} className="garden-gradient">
                <Crown className="h-4 w-4 mr-1" /> Unlock with Pro
              </Button>
            </CardContent>
          </Card>
        ) : view === 'garden' ? (
          <div className="space-y-6 print:space-y-4">
            <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Roboto, sans-serif' }}>
              Whole garden plan
            </h2>
            {beds.length === 0 ? (
              <p className="text-sm text-muted-foreground">No beds yet.</p>
            ) : (
              beds.map((bed) => {
                const d = designs[bed.id];
                return (
                  <Card key={bed.id} className="print:break-inside-avoid">
                    <CardHeader>
                      <CardTitle className="text-base">
                        {bed.name}{' '}
                        <span className="text-xs font-normal text-muted-foreground">
                          — {bed.length_cm}×{bed.width_cm} cm · {SUN_LABEL[bed.sun_exposure]}
                          {d?.templateName ? ` · ${d.templateName}` : ''}
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {d && d.placements.length > 0 ? (
                        <BedDiagram bed={d.spec} placements={d.placements} plan={d.plan} />
                      ) : (
                        <p className="text-sm text-muted-foreground italic">No design saved yet.</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        ) : (
          <>
            {/* Beds list */}
            <Card className="print:hidden">
              <CardHeader className="flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base">Your beds</CardTitle>
                <Dialog open={showNewBed} onOpenChange={setShowNewBed}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="garden-gradient">
                      <Plus className="h-4 w-4 mr-1" /> Add bed
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add a growing bed</DialogTitle>
                      <DialogDescription>Tell us its size and how much sun it gets.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="bed-name">Name</Label>
                        <Input id="bed-name" placeholder="e.g. Back raised bed"
                          value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="bed-length">Length (cm)</Label>
                          <Input id="bed-length" type="number" inputMode="numeric" min={30} max={2000}
                            value={form.length_cm === 0 ? '' : form.length_cm}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => {
                              const v = e.target.value;
                              setForm((f) => ({ ...f, length_cm: v === '' ? 0 : Number(v) }));
                            }} />
                        </div>
                        <div>
                          <Label htmlFor="bed-width">Width (cm)</Label>
                          <Input id="bed-width" type="number" inputMode="numeric" min={30} max={2000}
                            value={form.width_cm === 0 ? '' : form.width_cm}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => {
                              const v = e.target.value;
                              setForm((f) => ({ ...f, width_cm: v === '' ? 0 : Number(v) }));
                            }} />
                        </div>
                      </div>
                      <div>
                        <Label>Sun exposure</Label>
                        <Select value={form.sun_exposure} onValueChange={(v) => setForm((f) => ({ ...f, sun_exposure: v }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(SUN_LABEL).map(([v, l]) => (
                              <SelectItem key={v} value={v}>{l}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="bed-notes">Soil notes (optional)</Label>
                        <Textarea id="bed-notes" placeholder="e.g. heavy clay, recently mulched"
                          value={form.soil_notes}
                          onChange={(e) => setForm((f) => ({ ...f, soil_notes: e.target.value }))} />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="ghost" onClick={() => setShowNewBed(false)}>Cancel</Button>
                      <Button onClick={handleCreateBed} className="garden-gradient">Save bed</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {beds.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    No beds yet. Add your first growing space to start designing.
                  </p>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {beds.map((bed) => {
                      const isSelected = bed.id === selectedBedId;
                      const d = designs[bed.id];
                      return (
                        <button
                          key={bed.id}
                          type="button"
                          onClick={() => setSelectedBedId(bed.id)}
                          className={`text-left rounded-xl border p-4 transition-all ${
                            isSelected ? 'border-primary bg-primary/5 shadow-soft' : 'border-border/60 hover:border-border'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <h3 className="font-semibold text-foreground flex items-center gap-2 flex-wrap">
                                {bed.name}
                                {isSelected && <Badge variant="default" className="text-[10px]">Selected</Badge>}
                                {d?.templateName && <Badge variant="outline" className="text-[10px]">{d.templateName}</Badge>}
                              </h3>
                              <p className="text-xs text-muted-foreground">
                                {bed.length_cm}×{bed.width_cm} cm · {SUN_LABEL[bed.sun_exposure]}
                              </p>
                              {bed.soil_notes && (
                                <p className="text-xs text-muted-foreground mt-1 italic">{bed.soil_notes}</p>
                              )}
                            </div>
                            <span
                              role="button" tabIndex={0}
                              onClick={(e) => { e.stopPropagation(); handleDeleteBed(bed.id); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleDeleteBed(bed.id); } }}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-destructive cursor-pointer"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Design CTA */}
            {selectedBed && (
              <Card>
                <CardContent className="pt-5 pb-5 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Designing <span className="font-semibold">{selectedBed.name}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Pick a hand-curated guild around your hero crop. We'll lay out every plant in its real position.
                    </p>
                  </div>
                  <Button onClick={openWizard} className="w-full garden-gradient">
                    <Wand2 className="h-4 w-4 mr-1" />
                    {design ? 'Redesign this bed' : 'Design this bed'}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Result */}
            {design && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                    <span>{design.bedName ?? 'Bed'}</span>
                    {design.templateName && <Badge variant="outline">{design.templateName}</Badge>}
                    {design.density && <Badge variant="secondary" className="text-[10px] capitalize">{design.density}</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {design.placements.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nothing was placed in this bed.</p>
                  ) : (
                    <BedDiagram bed={design.spec} placements={design.placements} plan={design.plan} />
                  )}

                  {design.plan.length > 0 && (
                    <div className="space-y-2">
                      {design.plan.map((item) => (
                        <div key={item.legendIndex} className="flex items-start justify-between gap-2 rounded-lg border border-border/60 p-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-border/60 text-[10px] font-bold">
                                {item.legendIndex}
                              </span>
                              <span className="font-medium text-foreground">{item.name}</span>
                              {!item.fromUserGarden && <Badge variant="outline" className="text-[10px]">Companion</Badge>}
                              <Badge variant={item.sowMethod === 'transplant' ? 'secondary' : 'outline'} className="text-[10px]">
                                {item.sowMethod === 'transplant' ? 'Plant out' : 'Sow direct'}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">{item.notes}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-sm font-semibold text-foreground">×{item.quantity}</div>
                            <div className="text-[11px] text-muted-foreground">{item.spacing_cm} cm apart</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {design.rationale.length > 0 && (
                    <div className="rounded-lg bg-muted/30 p-3">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Why this works</p>
                      <ul className="space-y-1.5 text-sm text-foreground">
                        {design.rationale.map((r, i) => (
                          <li key={i} className="flex gap-2">
                            <Sprout className="h-4 w-4 text-primary mt-0.5 shrink-0" /> <span>{r}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {design.warnings.length > 0 && (
                    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                      <p className="text-xs font-semibold text-destructive uppercase tracking-wide mb-2">Notes</p>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        {design.warnings.map((w, i) => <li key={i}>• {w}</li>)}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>

      {/* WIZARD */}
      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          {wizardStep === 'hero' && (
            <>
              <DialogHeader>
                <DialogTitle>Pick a hero crop</DialogTitle>
                <DialogDescription>
                  Choose the star of this bed. We'll wrap it in a proven guild.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {(() => {
                  // Dedupe heroes by singular root so "Tomato"/"Tomatoes" collapse into one button.
                  // Prefer the variant the user already has in their garden; otherwise prefer the
                  // shorter (usually singular) form.
                  const canonical = (s: string) => s.toLowerCase().replace(/(ies|es|s)$/i, '');
                  const buckets = new Map<string, string[]>();
                  for (const t of TEMPLATES) {
                    for (const h of t.heroes) {
                      const k = canonical(h);
                      if (!buckets.has(k)) buckets.set(k, []);
                      buckets.get(k)!.push(h);
                    }
                  }
                  const display: string[] = [];
                  for (const variants of buckets.values()) {
                    const inGardenMatch = variants.find((v) =>
                      userPlantNames.some((n) => n.toLowerCase() === v.toLowerCase()),
                    );
                    const pick =
                      inGardenMatch ??
                      [...variants].sort((a, b) => a.length - b.length)[0];
                    display.push(pick);
                  }
                  display.sort((a, b) => {
                    const aIn = userPlantNames.some((n) => n.toLowerCase() === a.toLowerCase());
                    const bIn = userPlantNames.some((n) => n.toLowerCase() === b.toLowerCase());
                    if (aIn !== bIn) return aIn ? -1 : 1;
                    return a.localeCompare(b);
                  });
                  return display.map((heroName) => {
                    const usedElsewhere = heroesUsedElsewhere.has(heroName.toLowerCase());
                    const inGarden = userPlantNames.some((n) => n.toLowerCase() === heroName.toLowerCase());
                    const img = getPlantImage(heroName);
                    return (
                      <button
                        key={heroName}
                        type="button"
                        disabled={usedElsewhere}
                        onClick={() => handlePickHero(heroName)}
                        className={`flex flex-col items-center gap-1 rounded-xl border p-3 text-center transition ${
                          usedElsewhere
                            ? 'opacity-40 cursor-not-allowed border-border/40'
                            : 'border-border/60 hover:border-primary hover:bg-primary/5'
                        }`}
                      >
                        {img ? (
                          <img src={img} alt="" width={40} height={40} className="object-contain" loading="lazy" />
                        ) : (
                          <span className="text-3xl">{getPlantEmoji(heroName)}</span>
                        )}
                        <span className="text-xs font-medium text-foreground">{heroName}</span>
                        {usedElsewhere && <span className="text-[10px] text-muted-foreground">Used in another bed</span>}
                        {!usedElsewhere && inGarden && <span className="text-[10px] text-primary">In your garden</span>}
                      </button>
                    );
                  });
                })()}
              </div>
            </>
          )}

          {wizardStep === 'density' && wizardTemplate && (
            <>
              <DialogHeader>
                <DialogTitle>{wizardTemplate.name}</DialogTitle>
                <DialogDescription>{wizardTemplate.summary}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <Label>Planting density</Label>
                <div className="space-y-2">
                  {(['sparse', 'balanced', 'packed'] as Density[]).map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setWizardDensity(d)}
                      className={`w-full text-left rounded-lg border p-3 transition ${
                        wizardDensity === d
                          ? 'border-primary bg-primary/5'
                          : 'border-border/60 hover:border-border'
                      }`}
                    >
                      <p className="text-sm font-medium capitalize text-foreground">{d}</p>
                      <p className="text-xs text-muted-foreground">{DENSITY_LABEL[d]}</p>
                    </button>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setWizardStep('hero')}>Back</Button>
                <Button onClick={proceedToMissingOrBuild} disabled={busy} className="garden-gradient">
                  {busy ? 'Designing…' : 'Continue'}
                </Button>
              </DialogFooter>
            </>
          )}

          {wizardStep === 'missing' && wizardTemplate && wizardHero && (
            <>
              <DialogHeader>
                <DialogTitle>Missing companions</DialogTitle>
                <DialogDescription>
                  This guild works best with these plants too. Add them to your garden, or skip and we'll
                  build without.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                {missingCompanions(wizardTemplate, userPlantNames, wizardHero)
                  .filter((s) => !s.required)
                  .map((s) => {
                    const img = getPlantImage(s.name);
                    const decision = wizardDecisions[s.name] ?? 'skip';
                    return (
                      <div key={s.name} className="rounded-lg border border-border/60 p-3 space-y-2">
                        <div className="flex items-start gap-3">
                          {img ? (
                            <img src={img} alt="" width={32} height={32} className="object-contain shrink-0" loading="lazy" />
                          ) : (
                            <span className="text-2xl shrink-0">{getPlantEmoji(s.name)}</span>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground">{s.name}</p>
                            <p className="text-xs text-muted-foreground">{s.why}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant={decision === 'add' ? 'default' : 'outline'}
                            onClick={() => setWizardDecisions((p) => ({ ...p, [s.name]: 'add' }))}
                            className="flex-1 h-8 text-xs"
                          >
                            Add to garden
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={decision === 'skip' ? 'default' : 'outline'}
                            onClick={() => setWizardDecisions((p) => ({ ...p, [s.name]: 'skip' }))}
                            className="flex-1 h-8 text-xs"
                          >
                            Skip
                          </Button>
                        </div>
                      </div>
                    );
                  })}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setWizardStep('density')}>Back</Button>
                <Button onClick={finishMissingStep} disabled={busy} className="garden-gradient">
                  {busy ? 'Designing…' : 'Build this bed'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <UpgradeDialog open={showUpgrade} onOpenChange={setShowUpgrade} />
      <LegalFooter />
    </div>
  );
}
